import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import HFHeader from "@/components/HFHeader";
import HFFooter from "@/components/HFFooter";
import ImmersiveGallery, { type ImmersiveItem } from "@/components/ImmersiveGallery";
import SmartMedia from "@/components/SmartMedia";
import RichText from "@/components/RichText";
import { canonical, absoluteAsset } from "@/lib/siteConfig";
import HeroCarousel, { heroItemsWithFallback, type HeroItem } from "@/components/HeroCarousel";
import { Container, HFButton, HFLinkButton, SectionHeader } from "@/components/ui-hf";
import { useSiteContent, isEnabled } from "@/lib/siteContent";
import { resolveMediaUrl, type MediaRef } from "@/lib/media";
import { useReferenceProjects, formatMonthYearDe, ReferenceProject } from "@/lib/referenceProjects";
import BeforeAfterSection from "@/components/BeforeAfterSection";
import TrustBoxSection from "@/components/TrustBoxSection";
import DiscretionSection from "@/components/DiscretionSection";
import HomeGallery from "@/components/HomeGallery";
import {
    SYLT_HERO, SYLT_PROLOG, SYLT_VIEWER, SYLT_USECASES, SYLT_WHY,
    SYLT_REFERENCE, SYLT_STEPS, SYLT_PAKETE, SYLT_CONTACT,
    SYLT_BEFOREAFTER, SYLT_BA_PAIRS, SYLT_IMG,
    SYLT_V1_HERO, SYLT_V1_PROLOG, SYLT_V1_USECASES,
} from "@/lib/syltContent";
import {
    ArrowRight, Play, Home, Camera, MapPin, Anchor, Mountain,
    Clock, Waves, MoveHorizontal,
} from "lucide-react";

const ICONS: Record<string, React.ComponentType<{ size?: number | string }>> = {
    home: Home, camera: Camera, "map-pin": MapPin, anchor: Anchor,
    mountain: Mountain, clock: Clock, waves: Waves,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;
const DEMO_VIDEO_SRC = "/media/demo-360-web.mp4";
const DEMO_VIDEO_POSTER = "/media/demo-360-poster.jpg";

export default function SyltPage() {
    const { get, getObject, sectionEnabled } = useSiteContent();
    const { items: referenceProjects } = useReferenceProjects(6, "sylt");

    // Globale Section-Sichtbarkeit (Draft-Feld `enabled: false` in site_content).
    // Default = sichtbar; nur explizites `false` blendet aus. Identische Semantik
    // wie Home. Der Editor sitzt in AdminSylt.
    const showHero = sectionEnabled("sylt.hero");
    const showProlog = sectionEnabled("sylt.prolog");
    const showViewer = sectionEnabled("sylt.viewer");
    const showUseCases = sectionEnabled("sylt.use_cases");
    const showWhy = sectionEnabled("sylt.why");
    const showBeforeAfter = sectionEnabled("sylt.before_after");
    const showGallery = sectionEnabled("sylt.gallery");
    const showReference = sectionEnabled("sylt.reference");
    const showProcess = sectionEnabled("sylt.process");
    const showPakete = sectionEnabled("sylt.pakete");
    const showContact = sectionEnabled("sylt.contact");

    // Phase-2A CMS Section-Overrides für Sylt (Media). Fallback = statisch.
    type SectionMedia = { path?: string; media_id?: string } | undefined;
    interface SyltHeroSection { background?: SectionMedia; items?: HeroItem[] }
    interface SyltViewerSection { video?: SectionMedia; poster?: SectionMedia; items?: unknown[] }
    interface SyltRefSection { frames?: { label?: string; image?: SectionMedia }[] }
    interface SyltBASection { pairs?: { id?: string; title?: string; vorher?: SectionMedia; nachher?: SectionMedia }[] }
    interface SyltGallerySection { tiles?: { image?: SectionMedia; label?: string }[] }
    interface SyltUseCasesSection { tiles?: { image?: SectionMedia }[] }

    const cmsSyltHero = getObject<SyltHeroSection>("sylt.hero", {});
    const cmsSyltViewer = getObject<SyltViewerSection>("sylt.viewer", {});
    const cmsSyltRef = getObject<SyltRefSection>("sylt.reference", {});
    const cmsSyltBA = getObject<SyltBASection>("sylt.before_after", {});
    const cmsSyltGallery = getObject<SyltGallerySection>("sylt.gallery", {});
    const cmsSyltUC = getObject<SyltUseCasesSection>("sylt.use_cases", {});

    // Layout-Version: v1 (Original vor 24.07.2026) oder v2 (neue Fassung, Default).
    const layout = get<string>("sylt.layout.version", "v2") === "v1" ? "v1" : "v2";
    const isV1 = layout === "v1";

    // Aktive Content-Konstanten je Version (v1 = eingefrorenes Original, v2 = neu)
    const HERO_C = isV1 ? SYLT_V1_HERO : SYLT_HERO;
    const PROLOG_C = isV1 ? SYLT_V1_PROLOG : SYLT_PROLOG;
    const USECASES_C = isV1 ? SYLT_V1_USECASES : SYLT_USECASES;
    // Reference-Cover-Frame ist v1 = hero.jpg, v2 = szene-villa-terrasse.
    // Phase 2A: CMS-Media-Overrides pro Frame-Index falls in sylt.reference.frames vorhanden.
    const _refFallback = isV1
        ? [
            { label: "360° Foto", image: SYLT_IMG.hero },
            { label: "Fisheye", image: SYLT_IMG.house },
            { label: "Tiny Planet", image: SYLT_IMG.strandkorb },
        ]
        : SYLT_REFERENCE.frames;
    const referenceFrames = _refFallback
        .map((f, i) => ({
            label: f.label,
            image: resolveMediaUrl(cmsSyltRef.frames?.[i]?.image as MediaRef, f.image),
            _enabled: isEnabled(cmsSyltRef.frames?.[i] ?? {}),
        }))
        .filter((f) => f._enabled)
        .map(({ label, image }) => ({ label, image }));

    // CMS-Overlays: neue Section-JSONs (Phase 3B) haben Vorrang vor flat-Keys.
    const pick = (obj: Record<string, unknown> | undefined, k: string, flatKey: string, dflt: string) => {
        const v = obj?.[k];
        if (typeof v === "string" && v.trim()) return v;
        return get<string>(flatKey, dflt);
    };
    const cmsSyltHeroObj = cmsSyltHero as unknown as Record<string, unknown>;
    const cmsSyltViewerObj = cmsSyltViewer as unknown as Record<string, unknown>;
    const cmsSyltRefObj = cmsSyltRef as unknown as Record<string, unknown>;
    const cmsSyltBAObj = cmsSyltBA as unknown as Record<string, unknown>;
    const cmsSyltProlog = getObject<Record<string, unknown>>("sylt.prolog", {});
    const cmsSyltContact = getObject<Record<string, unknown>>("sylt.contact", {});
    // Phase 3B — Editor-Sections für die restlichen Textblöcke
    const cmsSyltUCObj = cmsSyltUC as unknown as Record<string, unknown>;
    const cmsSyltWhy = getObject<Record<string, unknown>>("sylt.why", {});
    const cmsSyltProcess = getObject<Record<string, unknown>>("sylt.process", {});
    const cmsSyltPakete = getObject<Record<string, unknown>>("sylt.pakete", {});

    const heroEyebrow = pick(cmsSyltHeroObj, "eyebrow", "sylt.hero.eyebrow", HERO_C.eyebrow);
    // Neuer Titel: sylt.hero.headline (Section-Object). Legacy flat-Keys kompatibel.
    const heroTitle = pick(cmsSyltHeroObj, "headline", "sylt.hero.headline",
        get<string>("sylt.hero.title", HERO_C.headline));
    const heroSubline = pick(cmsSyltHeroObj, "subline", "sylt.hero.subline", HERO_C.subline);
    // Hero-Bild: CMS-Media (media_id) hat Vorrang, dann Legacy-String-Key, dann statisch.
    const heroImageLegacy = get<string>("sylt.hero.image", "/media/sylt/hero.jpg");
    const heroImage = resolveMediaUrl(cmsSyltHero.background as MediaRef, heroImageLegacy);
    const heroTrust = pick(cmsSyltHeroObj, "trust_line", "sylt.hero.trust", HERO_C.trustLine);
    const prologBody = pick(cmsSyltProlog, "body", "sylt.prolog.body", PROLOG_C.body).trim();
    // Viewer-Section (bisher: nur body via pick; Rest hart aus SYLT_VIEWER)
    const viewerEyebrow = pick(cmsSyltViewerObj, "eyebrow", "sylt.viewer.eyebrow", SYLT_VIEWER.eyebrow);
    const viewerHeadlineA = pick(cmsSyltViewerObj, "headline_a", "sylt.viewer.headline_a", SYLT_VIEWER.headlineA);
    const viewerHeadlineB = pick(cmsSyltViewerObj, "headline_b", "sylt.viewer.headline_b", SYLT_VIEWER.headlineB);
    const viewerDemoBadge = pick(cmsSyltViewerObj, "demo_badge", "sylt.viewer.demo_badge", SYLT_VIEWER.demoBadge);
    const viewerBody = pick(cmsSyltViewerObj, "body", "sylt.viewer.body", SYLT_VIEWER.body);
    const referenceBody = pick(cmsSyltRefObj, "body", "sylt.reference.body", SYLT_REFERENCE.body);
    const referenceQuote = pick(cmsSyltRefObj, "quote", "sylt.reference.quote", SYLT_REFERENCE.quote);
    const referenceAuthor = pick(cmsSyltRefObj, "author", "sylt.reference.author", SYLT_REFERENCE.author);
    const contactBody = pick(cmsSyltContact, "body", "sylt.contact.body", SYLT_CONTACT.body);
    const contactEmail = pick(cmsSyltContact, "email", "sylt.contact.email", SYLT_CONTACT.email);
    const contactAddress = pick(cmsSyltContact, "address_placeholder", "sylt.contact.address", SYLT_CONTACT.addressPlaceholder);

    // Vorher/Nachher CMS-Overlays
    const baEyebrow = pick(cmsSyltBAObj, "eyebrow", "sylt.beforeafter.eyebrow", SYLT_BEFOREAFTER.eyebrow);
    const baTitle = pick(cmsSyltBAObj, "headline", "sylt.beforeafter.title", SYLT_BEFOREAFTER.title);
    const baLead = pick(cmsSyltBAObj, "lead", "sylt.beforeafter.lead", SYLT_BEFOREAFTER.lead);
    const baFootnote = pick(cmsSyltBAObj, "footnote", "sylt.beforeafter.footnote", SYLT_BEFOREAFTER.footnote);

    // Use-Cases · Titel-Bereich
    const ucEyebrow = pick(cmsSyltUCObj, "eyebrow", "sylt.use_cases.eyebrow", "Möglichkeiten");
    const ucHeadline = pick(cmsSyltUCObj, "headline", "sylt.use_cases.headline", "");
    type UcTile = { title?: string; body?: string; alt?: string; iconKey?: string; image?: MediaRef; enabled?: boolean };
    const ucTiles = ((cmsSyltUC.tiles || []) as UcTile[]).filter(isEnabled);
    const useCaseItems = USECASES_C.map((c, i) => {
        const cms = ucTiles[i];
        return {
            title: (cms?.title && cms.title.trim()) || c.title,
            body: (cms?.body && cms.body.trim()) || c.body,
            alt: cms?.alt || c.alt,
            iconKey: cms?.iconKey || c.iconKey,
            image: resolveMediaUrl(cms?.image as MediaRef, c.image),
            imageRef: cms?.image as MediaRef,
        };
    });

    // Why · Titel-Bereich + Blöcke
    const whyEyebrow = pick(cmsSyltWhy, "eyebrow", "sylt.why.eyebrow", "Warum wir auf die Insel kommen");
    const whyHeadline = pick(cmsSyltWhy, "headline", "sylt.why.headline", "");
    type WhyBlock = { title?: string; body?: string; iconKey?: string; enabled?: boolean };
    const whyBlocksRaw = Array.isArray(cmsSyltWhy.blocks) ? (cmsSyltWhy.blocks as WhyBlock[]).filter(isEnabled) : [];
    const whyBlocks = whyBlocksRaw.length > 0
        ? whyBlocksRaw.map((b, i) => ({
            title: b.title || SYLT_WHY[i]?.title || "",
            body: b.body || SYLT_WHY[i]?.body || "",
            iconKey: b.iconKey || SYLT_WHY[i]?.iconKey || "map-pin",
        }))
        : SYLT_WHY;

    // Reference-Titel (bisher hart aus SYLT_REFERENCE)
    const refEyebrow = pick(cmsSyltRefObj, "eyebrow", "sylt.reference.eyebrow", SYLT_REFERENCE.eyebrow);
    const refHeadlineA = pick(cmsSyltRefObj, "headline_a", "sylt.reference.headline_a", SYLT_REFERENCE.headlineA);
    const refHeadlineB = pick(cmsSyltRefObj, "headline_b", "sylt.reference.headline_b", SYLT_REFERENCE.headlineB);

    // Ablauf-Section (Titel + Schritte)
    const stepsEyebrow = pick(cmsSyltProcess, "eyebrow", "sylt.process.eyebrow", "Ablauf");
    const stepsHeadline = pick(cmsSyltProcess, "headline", "sylt.process.headline", "");
    type StepIt = { n?: string; title?: string; body?: string; enabled?: boolean };
    const stepsRaw = Array.isArray(cmsSyltProcess.steps) ? (cmsSyltProcess.steps as StepIt[]).filter(isEnabled) : [];
    const stepsItems = stepsRaw.length > 0
        ? stepsRaw.map((s, i) => ({
            n: s.n || SYLT_STEPS[i]?.n || String(i + 1).padStart(2, "0"),
            title: s.title || SYLT_STEPS[i]?.title || "",
            body: s.body || SYLT_STEPS[i]?.body || "",
        }))
        : SYLT_STEPS;

    // Pakete
    const paketeEyebrow = pick(cmsSyltPakete, "eyebrow", "sylt.pakete.eyebrow", "Pakete");
    const paketeHeadline = pick(cmsSyltPakete, "headline", "sylt.pakete.headline", "");
    type PkgIt = { name?: string; line?: string; body?: string; featured?: boolean; enabled?: boolean };
    const paketeRaw = Array.isArray(cmsSyltPakete.items) ? (cmsSyltPakete.items as PkgIt[]).filter(isEnabled) : [];
    const paketeItems = paketeRaw.length > 0
        ? paketeRaw.map((p, i) => ({
            name: p.name || SYLT_PAKETE[i]?.name || "Paket",
            line: p.line || SYLT_PAKETE[i]?.line || "",
            body: p.body || SYLT_PAKETE[i]?.body || "",
            featured: p.featured !== undefined ? !!p.featured : !!SYLT_PAKETE[i]?.featured,
        }))
        : SYLT_PAKETE;

    // Contact-Titel (bisher hart aus SYLT_CONTACT)
    const contactEyebrow = pick(cmsSyltContact, "eyebrow", "sylt.contact.eyebrow", SYLT_CONTACT.eyebrow);
    const contactHeadlineA = pick(cmsSyltContact, "headline_a", "sylt.contact.headline_a", SYLT_CONTACT.headlineA);
    const contactHeadlineB = pick(cmsSyltContact, "headline_b", "sylt.contact.headline_b", SYLT_CONTACT.headlineB);
    // Pairs: CMS-Pairs überschreiben statische (Anzahl + Reihenfolge)
    const cmsPairsArr = (cmsSyltBA.pairs || []).filter(isEnabled).filter((p) => p.vorher && p.nachher);
    const baPairs = cmsPairsArr.length > 0
        ? cmsPairsArr.map((p, i) => ({
            id: p.id || `cms-${i}`,
            title: p.title || `Paar ${i + 1}`,
            vorher: resolveMediaUrl(p.vorher as MediaRef, ""),
            nachher: resolveMediaUrl(p.nachher as MediaRef, ""),
        }))
        : SYLT_BA_PAIRS.map((p, i) => ({
            ...p,
            vorher: resolveMediaUrl(cmsSyltBA.pairs?.[i]?.vorher as MediaRef, p.vorher),
            nachher: resolveMediaUrl(cmsSyltBA.pairs?.[i]?.nachher as MediaRef, p.nachher),
            title: get<string>(`sylt.beforeafter.pair${i + 1}.title`, cmsSyltBA.pairs?.[i]?.title || p.title),
        }));

    const canonicalUrl = canonical("/sylt");
    const heroImageAbs = absoluteAsset(heroImage);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "HypeFly Sylt",
        image: heroImageAbs,
        url: canonicalUrl,
        telephone: "+49 211 61 66 33 00",
        email: contactEmail,
        description:
            "360°-Video-Dokumentation für Reetdach-Villen, Boutique-Hotels und Familien-Refugien auf Sylt — ergänzt um hochwertige 360°-Fotos. Persönliche Anreise, Wetterfenster-Planung, diskret seit 2004.",
        serviceType: "360°-Video-Immobiliendokumentation",
        areaServed: [
            { "@type": "Place", name: "Sylt" },
            { "@type": "Place", name: "Westerland" },
            { "@type": "Place", name: "Kampen" },
            { "@type": "Place", name: "Wenningstedt" },
            { "@type": "Place", name: "Rantum" },
            { "@type": "Place", name: "Hörnum" },
            { "@type": "Place", name: "List" },
            { "@type": "Place", name: "Keitum" },
        ],
        parentOrganization: {
            "@type": "Organization",
            name: "BDV Büroservice Dienstleistungs-Vermittlungs GmbH",
            url: "https://www.b-d-v.de/",
        },
    };

    return (
        <div className="min-h-screen">
            <Helmet>
                <title>HypeFly Sylt — Sylt bleibt. 360°-Video-Dokumentation für Reetdach-Villen &amp; Insel-Refugien</title>
                <meta
                    name="description"
                    content="Sylt bleibt. Insel im Wandel, von uns dauerhaft bewahrt. 360°-Video-Dokumentation für Reetdach-Villen, Boutique-Hotels und Familien-Refugien auf Sylt — ergänzt um 360°-Fotos. Anreise inklusive. Diskret seit 2004."
                />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="HypeFly Sylt · Sylt bleibt." />
                <meta property="og:description" content="Insel im Wandel. Von uns dauerhaft bewahrt. Reetdach-Villen, Boutique-Hotels, Familien-Refugien." />
                <meta property="og:image" content={heroImageAbs} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:locale" content="de_DE" />
                <meta name="twitter:card" content="summary_large_image" />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>
            <HFHeader />
            <main id="main-content" data-testid="sylt-main" data-layout={layout}>
                {showHero && <SyltHero
                    eyebrow={heroEyebrow}
                    headline={heroTitle}
                    subline={heroSubline}
                    image={heroImage}
                    heroItems={heroItemsWithFallback(cmsSyltHero.items, cmsSyltHero.background as MediaRef)}
                    trust={heroTrust}
                />}
                {showProlog && <SyltProlog body={prologBody} prolog={PROLOG_C} decorative={!isV1} />}
                {/* Trust-Box — identisch zur Startseite. Nutzt den gleichen
                 *  CMS-Content (`home.trust_box`); der `enabled`-Toggle im
                 *  Admin (`/admin/vertrauen`) steuert damit Startseite und
                 *  Sylt gemeinsam. Section-Padding (py-4 md:py-6) und
                 *  Card/Editorial-Variante folgen automatisch der bestehenden
                 *  TrustBoxSection-Komponente. */}
                <TrustBoxSection />
                {showViewer && <SyltViewer
                    eyebrow={viewerEyebrow}
                    headlineA={viewerHeadlineA}
                    headlineB={viewerHeadlineB}
                    demoBadge={viewerDemoBadge}
                    body={viewerBody}
                    items={(() => {
                        // Neues Multi-Item-Schema (bis zu 4 Kacheln). Fallback:
                        // wenn `items` fehlt aber Legacy-`video`/`poster` gesetzt sind,
                        // synthetisiere items[0] daraus. Alle inaktiven werden
                        // ausgefiltert.
                        const rawItems = (cmsSyltViewer.items as ImmersiveItem[] | undefined) || [];
                        let items = rawItems.filter(isEnabled).slice(0, 4);
                        if (items.length === 0 && cmsSyltViewer.video) {
                            items = [{
                                id: "legacy",
                                title: "Refugium · Rundgang",
                                enabled: true,
                                video: cmsSyltViewer.video as MediaRef,
                                poster: cmsSyltViewer.poster as MediaRef,
                            }];
                        }
                        if (items.length === 0) {
                            items = [{
                                id: "demo",
                                title: "Demo",
                                enabled: true,
                                video: DEMO_VIDEO_SRC as unknown as MediaRef,
                                poster: DEMO_VIDEO_POSTER as unknown as MediaRef,
                            }];
                        }
                        return items;
                    })()}
                />}
                {/* videoActive/onActivate entfernt — der Viewer im Video360Viewer/
                    ImmersiveGallery-Solo-Modus zeigt sein eigenes Play-Overlay. */}
                {showUseCases && <SyltUseCases
                    eyebrow={ucEyebrow}
                    headline={ucHeadline}
                    items={useCaseItems}
                    showFootnote={!isV1}
                />}
                {showWhy && <SyltWhy eyebrow={whyEyebrow} headline={whyHeadline} blocks={whyBlocks} />}
                {!isV1 && showBeforeAfter && <BeforeAfterSection source="sylt" />}
                {!isV1 && showGallery && <SyltGallery
                    eyebrow={pick(cmsSyltGallery as unknown as Record<string, unknown>, "eyebrow", "sylt.gallery.eyebrow", "Insel-Impressionen")}
                    headline={pick(cmsSyltGallery as unknown as Record<string, unknown>, "headline", "sylt.gallery.headline", "Sylt in unseren Augen.")}
                />}
                {showReference && get<boolean>("sylt.show_kampen_example", false) && (
                    <SyltReference
                        eyebrow={refEyebrow}
                        headlineA={refHeadlineA}
                        headlineB={refHeadlineB}
                        body={referenceBody}
                        quote={referenceQuote}
                        author={referenceAuthor}
                        frames={referenceFrames}
                        projects={referenceProjects}
                    />
                )}
                {get<boolean>("sylt.show_discretion", false) && <DiscretionSection dark />}
                {showProcess && <SyltAblauf eyebrow={stepsEyebrow} headline={stepsHeadline} steps={stepsItems} />}
                {showPakete && <SyltPakete eyebrow={paketeEyebrow} headline={paketeHeadline} items={paketeItems} />}
                {showContact && <SyltContact
                    eyebrow={contactEyebrow}
                    headlineA={contactHeadlineA}
                    headlineB={contactHeadlineB}
                    body={contactBody}
                    email={contactEmail}
                    address={contactAddress}
                />}
            </main>
            <HFFooter />
        </div>
    );
}

/* ─────────────  HERO  ───────────── */
function SyltHero({
    eyebrow, headline, subline, image, heroItems, trust,
}: {
    eyebrow: string; headline: string; subline: string; image: string;
    heroItems: HeroItem[];
    trust: string;
}) {
    return (
        <section
            className="relative overflow-hidden"
            style={{ minHeight: "min(88vh, 900px)", background: "hsl(var(--hf-ink))" }}
            data-testid="sylt-hero"
        >
            <HeroCarousel
                items={heroItems}
                fallbackUrl={image}
                fallbackAlt="Reetdach-Villa auf Sylt im warmen Licht"
                testIdPrefix="sylt-hero-carousel"
                className="absolute inset-0 w-full h-full"
            />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, hsl(var(--hf-ink) / 0.15) 0%, hsl(var(--hf-ink) / 0.35) 55%, hsl(var(--hf-ink) / 0.92) 100%)",
                    pointerEvents: "none",
                }}
            />
            <SyltWatermark />
            <Container className="relative pt-28 md:pt-40 pb-20 md:pb-28 min-h-[88vh] flex flex-col justify-end">
                <div className="max-w-4xl">
                    <div
                        className="hf-eyebrow inline-flex items-center gap-3"
                        style={{ color: "hsl(var(--hf-gold-soft))" }}
                    >
                        <span
                            className="inline-block w-8 h-px"
                            style={{ background: "hsl(var(--hf-gold-soft))" }}
                        />
                        {eyebrow}
                    </div>
                    <h1
                        className="mt-6 font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.98] whitespace-pre-line"
                        style={{ color: "hsl(var(--hf-bg))" }}
                        data-testid="sylt-hero-headline"
                    >
                        {headline}
                    </h1>
                    <p
                        className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed"
                        style={{ color: "hsl(var(--hf-bg) / 0.88)" }}
                    >
                        {subline}
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <a
                            href="#sylt-viewer"
                            className="hf-btn hf-btn-primary inline-flex items-center gap-2"
                            data-testid="sylt-hero-cta-viewer"
                        >
                            <Play size={14} /> 360°-Video-Beispiel erleben
                        </a>
                        <a
                            href="#sylt-kontakt"
                            className="hf-btn hf-btn-ghost inline-flex items-center gap-2"
                            style={{ color: "hsl(var(--hf-bg))", borderColor: "hsl(var(--hf-bg) / 0.3)" }}
                            data-testid="sylt-hero-cta-contact"
                        >
                            Ihr Sylt-Objekt anfragen <ArrowRight size={14} />
                        </a>
                    </div>
                    <div
                        className="mt-12 flex flex-wrap gap-x-8 gap-y-2 text-[11px] tracking-[0.28em] uppercase"
                        style={{ color: "hsl(var(--hf-gold-soft))" }}
                    >
                        {trust.split("·").map((t, i) => (
                            <span key={i}>◇ {t.trim()}</span>
                        ))}
                    </div>
                </div>
            </Container>
        </section>
    );
}

/* ─────────────  PROLOG  ───────────── */
function SyltProlog({
    body, prolog, decorative,
}: {
    body: string;
    prolog: typeof SYLT_PROLOG;
    decorative: boolean;
}) {
    if (!decorative) {
        // v1-Layout: einspaltig, zentriert, ohne Bilder
        return (
            <section className="py-24 md:py-32 text-center" data-testid="sylt-prolog">
                <Container className="max-w-3xl">
                    <div className="hf-eyebrow">{prolog.eyebrow}</div>
                    <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                        {prolog.headlineA}
                        <br />
                        {prolog.headlineB}
                        <br />
                        <em
                            className="not-italic font-normal"
                            style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                        >
                            {prolog.headlineC}
                        </em>
                    </h2>
                    <p className="mt-10 text-base md:text-lg leading-relaxed text-hf-muted max-w-2xl mx-auto">
                        <RichText html={body} inline />
                    </p>
                </Container>
            </section>
        );
    }
    // v2-Layout: 2-spaltig mit Dünen-Bild + kleinem Ferienhaus-Overlay
    return (
        <section
            className="py-24 md:py-32"
            data-testid="sylt-prolog"
        >
            <Container>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-7 md:col-start-1">
                        <div className="hf-eyebrow">{prolog.eyebrow}</div>
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
                            {prolog.headlineA}
                            <br />
                            {prolog.headlineB}
                            <br />
                            <em
                                className="not-italic font-normal"
                                style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                            >
                                {prolog.headlineC}
                            </em>
                        </h2>
                        <p className="mt-8 text-base md:text-lg leading-relaxed text-hf-muted max-w-xl">
                            <RichText html={body} inline />
                        </p>
                    </div>
                    <div className="md:col-span-5 relative">
                        <div
                            className="relative aspect-[4/5] overflow-hidden rounded-sm"
                            style={{ border: "1px solid hsl(var(--hf-line))" }}
                        >
                            <img
                                src={SYLT_IMG.sceneDunes}
                                alt="Sylter Dünen im Sonnenuntergangslicht"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                            <span
                                className="absolute bottom-3 left-3 text-[9px] tracking-[0.22em] uppercase px-2 py-1 rounded-sm"
                                style={{
                                    background: "hsl(var(--hf-ink) / 0.78)",
                                    color: "hsl(var(--hf-gold-soft))",
                                }}
                            >
                                Sylt · Dünenlicht
                            </span>
                        </div>
                        <img
                            src="/media/sylt/uc-ferienhaus.jpg"
                            alt="Modernes Sylter Refugium mit Terrasse und Dünenblick im Sonnenuntergang"
                            loading="lazy"
                            className="hidden md:block absolute -bottom-8 -left-10 w-44 lg:w-56 aspect-[4/3] object-cover rounded-sm"
                            style={{
                                border: "1px solid hsl(var(--hf-line))",
                                boxShadow: "0 24px 48px -18px hsl(var(--hf-ink) / 0.28)",
                            }}
                        />
                    </div>
                </div>
            </Container>
        </section>
    );
}

/* ─────────────  360°-VIEWER (Multi-Item)  ───────────── */
function SyltViewer({
    eyebrow, headlineA, headlineB, demoBadge, body, items,
}: {
    eyebrow: string; headlineA: string; headlineB: string; demoBadge: string;
    body: string; items: ImmersiveItem[];
}) {
    return (
        <section
            id="sylt-viewer"
            className="py-24 md:py-32"
            style={{ background: "hsl(var(--hf-surface))" }}
            data-testid="sylt-viewer-section"
        >
            <Container>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
                    <div className="md:col-span-7">
                        <div className="hf-eyebrow">{eyebrow}</div>
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            {headlineA}
                            <br />
                            <em
                                className="not-italic"
                                style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                            >
                                {headlineB}
                            </em>
                        </h2>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-base md:text-lg leading-relaxed text-hf-muted"><RichText html={body} inline /></p>
                    </div>
                </div>

                {/* Demo-Badge nur bei Solo-Modus (1 Item) sinnvoll positioniert.
                    Bei Grid-Modus würde er über den Kacheln stehen — dort
                    zeigen wir ihn stattdessen zentriert oberhalb. */}
                {items.length === 1 ? (
                    <div
                        className="relative overflow-hidden rounded-sm"
                        style={{ border: "1px solid hsl(var(--hf-line))" }}
                        data-testid="sylt-viewer-frame"
                    >
                        <span
                            className="absolute z-30 top-3 left-3 md:top-4 md:left-4 text-[9px] md:text-[10px] tracking-[0.22em] uppercase px-2 py-1 rounded-sm"
                            style={{
                                background: "hsl(var(--hf-ink) / 0.78)",
                                color: "hsl(var(--hf-gold-soft))",
                                border: "1px solid hsl(var(--hf-gold-dark) / 0.5)",
                            }}
                            data-testid="sylt-viewer-demo-badge"
                        >
                            {demoBadge}
                        </span>
                        <ImmersiveGallery items={items} soloTestIdPrefix="sylt-video" />
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="sylt-viewer-frame">
                        {demoBadge && (
                            <div className="flex justify-center">
                                <span
                                    className="text-[9px] md:text-[10px] tracking-[0.22em] uppercase px-2 py-1 rounded-sm"
                                    style={{
                                        background: "hsl(var(--hf-ink) / 0.78)",
                                        color: "hsl(var(--hf-gold-soft))",
                                        border: "1px solid hsl(var(--hf-gold-dark) / 0.5)",
                                    }}
                                    data-testid="sylt-viewer-demo-badge"
                                >
                                    {demoBadge}
                                </span>
                            </div>
                        )}
                        <ImmersiveGallery items={items} soloTestIdPrefix="sylt-video" />
                    </div>
                )}
            </Container>
        </section>
    );
}

/* ─────────────  USE-CASES (5)  ───────────── */
type SyltUseCaseItem = {
    title: string; body: string; alt?: string; iconKey: string; image: string; imageRef?: MediaRef;
};
function SyltUseCases({
    eyebrow, headline, items, showFootnote,
}: {
    eyebrow: string;
    headline: string;
    items: SyltUseCaseItem[];
    showFootnote: boolean;
}) {
    return (
        <section
            className="py-24 md:py-32"
            data-testid="sylt-usecases"
        >
            <Container>
                <div className="max-w-3xl mb-14">
                    <div className="hf-eyebrow">{eyebrow}</div>
                    {headline ? (
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            {headline}
                        </h2>
                    ) : (
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            Fünf Sylter Anlässe.
                            <br />
                            <em
                                className="not-italic"
                                style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                            >
                                Ein Verfahren.
                            </em>
                        </h2>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                    {items.map((c, i) => {
                        const Icon = ICONS[c.iconKey] || Home;
                        return (
                            <article
                                key={i}
                                className="group"
                                data-testid={`sylt-usecase-${i}`}
                            >
                                <div
                                    className="aspect-[3/2] overflow-hidden mb-6 rounded-sm"
                                    style={{
                                        background: "hsl(var(--hf-ink))",
                                        border: "1px solid hsl(var(--hf-line))",
                                    }}
                                >
                                    <SmartMedia
                                        mediaRef={c.imageRef}
                                        fallbackUrl={c.image}
                                        alt={c.alt || c.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                                        testId={`sylt-usecase-media-${i}`}
                                        interactive360={true}
                                        videoAutoplay
                                        videoMuted
                                        videoLoop
                                        videoControls={false}
                                    />
                                </div>
                                <div className="flex items-start gap-4">
                                    <span
                                        className="w-10 h-10 flex-shrink-0 inline-flex items-center justify-center rounded-sm"
                                        style={{
                                            border: "1px solid hsl(var(--hf-gold))",
                                            color: "hsl(var(--hf-gold-dark))",
                                        }}
                                        aria-hidden
                                    >
                                        <Icon size={16} />
                                    </span>
                                    <div>
                                        <h3 className="font-display text-2xl leading-tight">{c.title}</h3>
                                        <p className="mt-2 text-sm md:text-base leading-relaxed text-hf-muted">
                                            <RichText html={c.body} inline />
                                        </p>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
                {showFootnote && (
                    <p
                        className="mt-14 text-xs md:text-sm text-hf-muted italic max-w-3xl"
                        data-testid="sylt-usecases-footnote"
                    >
                        Illustrationsbilder · Referenzaufnahmen von realisierten Sylter Projekten auf Anfrage.
                    </p>
                )}
            </Container>
        </section>
    );
}

/* ─────────────  WARUM WIR AUF DIE INSEL KOMMEN  ───────────── */
type SyltWhyItem = { title: string; body: string; iconKey: string };
function SyltWhy({
    eyebrow, headline, blocks,
}: {
    eyebrow: string; headline: string; blocks: SyltWhyItem[];
}) {
    return (
        <section
            className="py-24 md:py-32"
            style={{ background: "hsl(var(--hf-surface))" }}
            data-testid="sylt-why"
        >
            <Container className="max-w-4xl">
                <div className="mb-16 md:mb-20 text-center">
                    <div className="hf-eyebrow">{eyebrow}</div>
                    {headline ? (
                        <h2 className="mt-6 font-display text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">
                            {headline}
                        </h2>
                    ) : (
                        <h2 className="mt-6 font-display text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">
                            Vier Gründe, warum sich Sylt-Eigentümer für{" "}
                            <em
                                className="not-italic"
                                style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                            >
                                uns
                            </em>{" "}
                            entscheiden.
                        </h2>
                    )}
                </div>
                <div className="space-y-14 md:space-y-16">
                    {blocks.map((b, i) => {
                        const Icon = ICONS[b.iconKey] || Home;
                        return (
                            <div
                                key={i}
                                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start"
                                data-testid={`sylt-why-${i}`}
                            >
                                <div className="md:col-span-2 flex md:justify-center">
                                    <span
                                        className="w-14 h-14 inline-flex items-center justify-center rounded-sm"
                                        style={{
                                            border: "1px solid hsl(var(--hf-gold))",
                                            color: "hsl(var(--hf-gold-dark))",
                                        }}
                                        aria-hidden
                                    >
                                        <Icon size={20} />
                                    </span>
                                </div>
                                <div className="md:col-span-10">
                                    <div
                                        className="hf-eyebrow"
                                        style={{ color: "hsl(var(--hf-gold-dark))" }}
                                    >
                                        Prinzip 0{i + 1}
                                    </div>
                                    <h3 className="mt-3 font-display text-3xl md:text-4xl leading-tight">
                                        {b.title}
                                    </h3>
                                    <p className="mt-4 text-base md:text-lg leading-relaxed text-hf-muted max-w-2xl">
                                        <RichText html={b.body} inline />
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Container>
        </section>
    );
}

/* ─────────────  REFERENCE CASE  ───────────── */
function SyltReference({
    eyebrow, headlineA, headlineB, body, quote, author, frames, projects,
}: {
    eyebrow: string; headlineA: string; headlineB: string;
    body: string; quote: string; author: string;
    frames: typeof SYLT_REFERENCE.frames;
    projects: ReferenceProject[];
}) {
    const hasDynamic = projects.length > 0;
    return (
        <section
            className="py-24 md:py-32"
            style={{ background: "hsl(var(--hf-ink))", color: "hsl(var(--hf-bg))" }}
            data-testid="sylt-reference"
        >
            <Container>
                <div className="max-w-3xl mb-14">
                    <div
                        className="hf-eyebrow"
                        style={{ color: "hsl(var(--hf-gold-soft))" }}
                    >
                        {eyebrow}
                    </div>
                    <h2
                        className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
                        style={{ color: "hsl(var(--hf-bg))" }}
                    >
                        {headlineA}{" "}
                        <em
                            className="not-italic"
                            style={{ color: "hsl(var(--hf-gold-soft))", fontStyle: "italic" }}
                        >
                            {headlineB}
                        </em>
                    </h2>
                    <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl text-hf-bg/85">
                        <RichText html={body} inline />
                    </p>
                </div>

                {hasDynamic ? (
                    /* Dynamische Referenzprojekte aus dem CMS */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-12" data-testid="sylt-reference-projects">
                        {projects.map((p) => (
                            <Link
                                key={p.id}
                                to={`/projekt/${p.slug}`}
                                className="group block relative aspect-[4/5] overflow-hidden rounded-sm"
                                style={{ background: "hsl(var(--hf-bg))" }}
                                data-testid={`sylt-reference-project-${p.slug}`}
                            >
                                {p.cover_url && (
                                    <img
                                        src={p.cover_url}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                    />
                                )}
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: "linear-gradient(180deg, hsl(var(--hf-ink) / 0.10) 0%, hsl(var(--hf-ink) / 0.35) 55%, hsl(var(--hf-ink) / 0.88) 100%)",
                                    }}
                                />
                                {p.object_type && (
                                    <span
                                        className="absolute top-3 left-3 px-2 py-1 text-[9px] tracking-[0.22em] uppercase rounded-sm"
                                        style={{
                                            background: "hsl(var(--hf-ink) / 0.78)",
                                            color: "hsl(var(--hf-gold-soft))",
                                            border: "1px solid hsl(var(--hf-gold) / 0.5)",
                                        }}
                                    >
                                        {p.object_type}
                                    </span>
                                )}
                                <div className="absolute inset-x-4 bottom-4">
                                    <div className="font-display text-xl md:text-2xl leading-tight" style={{ color: "hsl(var(--hf-bg))" }}>
                                        {p.title}
                                    </div>
                                    <div className="mt-1 text-[11px] tracking-[0.16em] uppercase" style={{ color: "hsl(var(--hf-gold-soft))" }}>
                                        {[p.location, formatMonthYearDe(p.capture_date)].filter(Boolean).join(" · ")}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    /* Editorial-Platzhalter, wenn noch keine Referenzprojekte publiziert sind */
                    <div
                        className="mb-12 p-8 md:p-12 rounded-sm text-center"
                        style={{
                            border: "1px dashed hsl(var(--hf-gold) / 0.4)",
                            background: "hsl(var(--hf-ink) / 0.35)",
                        }}
                        data-testid="sylt-reference-empty"
                    >
                        <div className="hf-eyebrow" style={{ color: "hsl(var(--hf-gold-soft))" }}>
                            In Kürze
                        </div>
                        <p className="mt-3 font-display text-2xl md:text-3xl leading-snug max-w-2xl mx-auto" style={{ color: "hsl(var(--hf-bg))" }}>
                            Erste Referenzprojekte werden derzeit vorbereitet.
                        </p>
                    </div>
                )}

                <blockquote
                    className="pl-6 max-w-3xl"
                    style={{ borderLeft: "2px solid hsl(var(--hf-gold))" }}
                >
                    <p className="font-display text-2xl md:text-3xl italic leading-snug">
                        „{quote}&#8220;
                    </p>
                    <cite
                        className="mt-4 block text-[11px] tracking-[0.24em] uppercase not-italic"
                        style={{ color: "hsl(var(--hf-gold-soft))" }}
                    >
                        {author}
                    </cite>
                </blockquote>
            </Container>
        </section>
    );
}

/* ─────────────  ABLAUF  ───────────── */
type SyltStepItem = { n: string; title: string; body: string };
function SyltAblauf({
    eyebrow, headline, steps,
}: {
    eyebrow: string; headline: string; steps: SyltStepItem[];
}) {
    return (
        <section
            className="py-24 md:py-32"
            data-testid="sylt-ablauf"
        >
            <Container>
                <div className="max-w-3xl mb-14">
                    <div className="hf-eyebrow">{eyebrow}</div>
                    {headline ? (
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            {headline}
                        </h2>
                    ) : (
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            Fünf Schritte
                            <br />
                            <em
                                className="not-italic"
                                style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                            >
                                zum Zeitdokument.
                            </em>
                        </h2>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-6">
                    {steps.map((s) => (
                        <div
                            key={s.n}
                            className="pt-6"
                            style={{ borderTop: "1px solid hsl(var(--hf-gold))" }}
                            data-testid={`sylt-step-${s.n}`}
                        >
                            <div
                                className="font-display text-5xl"
                                style={{ color: "hsl(var(--hf-gold-dark))" }}
                            >
                                {s.n}
                            </div>
                            <h4 className="mt-4 font-display text-xl leading-tight">{s.title}</h4>
                            <p className="mt-3 text-sm md:text-base leading-relaxed text-hf-muted">
                                {s.body}
                            </p>
                        </div>
                    ))}
                </div>
            </Container>
        </section>
    );
}

/* ─────────────  PAKETE  ───────────── */
type SyltPaketItem = { name: string; line: string; body: string; featured?: boolean };
function SyltPakete({
    eyebrow, headline, items,
}: {
    eyebrow: string; headline: string; items: SyltPaketItem[];
}) {
    return (
        <section
            className="py-24 md:py-32"
            style={{ background: "hsl(var(--hf-surface))" }}
            data-testid="sylt-pakete"
        >
            <Container>
                <div className="max-w-3xl mb-14">
                    <div className="hf-eyebrow">{eyebrow}</div>
                    {headline ? (
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            {headline}
                        </h2>
                    ) : (
                        <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            Drei Konfigurationen.
                            <br />
                            <em
                                className="not-italic"
                                style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                            >
                                Jeweils mit Anreise.
                            </em>
                        </h2>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {items.map((p) => {
                        const feat = !!p.featured;
                        return (
                            <article
                                key={p.name}
                                className="p-8 md:p-10 flex flex-col rounded-sm"
                                style={{
                                    background: feat ? "hsl(var(--hf-ink))" : "hsl(var(--hf-bg))",
                                    color: feat ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                                    border: `1px solid ${feat ? "hsl(var(--hf-ink))" : "hsl(var(--hf-line))"}`,
                                }}
                                data-testid={`sylt-paket-${p.name.toLowerCase()}`}
                            >
                                <div
                                    className="hf-eyebrow"
                                    style={{
                                        color: feat ? "hsl(var(--hf-gold-soft))" : "hsl(var(--hf-gold-dark))",
                                    }}
                                >
                                    {p.line}
                                </div>
                                <h3 className="mt-5 font-display text-4xl">{p.name}</h3>
                                <p
                                    className="mt-5 text-sm md:text-base leading-relaxed flex-1"
                                    style={{ color: feat ? "hsl(var(--hf-bg) / 0.82)" : "hsl(var(--hf-muted))" }}
                                >
                                    {p.body}
                                </p>
                                <div
                                    className="mt-6 text-sm italic"
                                    style={{
                                        color: feat ? "hsl(var(--hf-gold-soft))" : "hsl(var(--hf-gold-dark))",
                                    }}
                                >
                                    Anreise &amp; Übernachtung inklusive.
                                </div>
                                <div
                                    className="mt-4 hf-eyebrow"
                                    style={{
                                        color: feat ? "hsl(var(--hf-gold-soft))" : "hsl(var(--hf-gold-dark))",
                                    }}
                                >
                                    Preis auf Anfrage
                                </div>
                                <a
                                    href="#sylt-kontakt"
                                    className={`mt-6 hf-btn inline-flex items-center justify-center gap-2 ${feat ? "hf-btn-primary" : "hf-btn-ghost"}`}
                                    data-testid={`sylt-paket-cta-${p.name.toLowerCase()}`}
                                >
                                    Unverbindlich anfragen <ArrowRight size={13} />
                                </a>
                            </article>
                        );
                    })}
                </div>
            </Container>
        </section>
    );
}

/* ─────────────  KONTAKT-CTA  ─────────────
   Bewusst KEIN eigenes Formular auf /sylt — Anfragen laufen konsolidiert
   in das bestehende Kontakt-Backend, mit ref=sylt-Marker, so dass das
   Team den Sylt-Kontext direkt in der Admin-Inbox erkennt. */
function SyltContact({
    eyebrow, headlineA, headlineB, body, email, address,
}: {
    eyebrow: string; headlineA: string; headlineB: string;
    body: string; email: string; address: string;
}) {
    return (
        <section
            id="sylt-kontakt"
            className="py-24 md:py-32"
            data-testid="sylt-contact"
        >
            <Container className="max-w-4xl">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
                    <div className="md:col-span-6">
                        <div className="hf-eyebrow">{eyebrow}</div>
                        <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.05]">
                            {headlineA}
                            <br />
                            <em
                                className="not-italic"
                                style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                            >
                                {headlineB}
                            </em>
                        </h2>
                        <p className="mt-6 text-base md:text-lg leading-relaxed text-hf-muted">
                            <RichText html={body} inline />
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/?ref=sylt#kontakt-formular"
                                className="hf-btn hf-btn-primary inline-flex items-center gap-2"
                                data-testid="sylt-contact-cta"
                            >
                                Anfrage senden <ArrowRight size={14} />
                            </Link>
                            <a
                                href={`mailto:${email}`}
                                className="hf-btn hf-btn-ghost inline-flex items-center gap-2"
                                data-testid="sylt-contact-email"
                            >
                                Direkt per E-Mail
                            </a>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div
                            className="p-6 md:p-8 rounded-sm space-y-4 text-sm md:text-base"
                            style={{
                                background: "hsl(var(--hf-surface))",
                                border: "1px solid hsl(var(--hf-line))",
                            }}
                            data-testid="sylt-contact-card"
                        >
                            <div>
                                <div className="hf-eyebrow" style={{ fontSize: "10px" }}>E-Mail Sylt</div>
                                <a
                                    className="mt-1 block text-hf-ink hover:text-hf-gold-dark transition-colors"
                                    href={`mailto:${email}`}
                                >
                                    {email}
                                </a>
                            </div>
                            <div>
                                <div className="hf-eyebrow" style={{ fontSize: "10px" }}>Postanschrift</div>
                                <div
                                    className="mt-1 text-hf-muted italic"
                                    data-testid="sylt-contact-address-placeholder"
                                >
                                    {address}
                                </div>
                            </div>
                            <div>
                                <div className="hf-eyebrow" style={{ fontSize: "10px" }}>Telefon</div>
                                <div className="mt-1 text-hf-muted">
                                    BDV-Zentrale · +49 211 61 66 33 00
                                </div>
                            </div>
                            <p className="text-xs italic text-hf-muted pt-2">
                                Anfragen werden über das zentrale HypeFly-Kontaktformular abgewickelt. Wir erkennen automatisch, dass Ihre Anfrage Sylt betrifft, und planen entsprechend das Wetterfenster.
                            </p>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    );
}

/* ─────────────  VORHER · NACHHER  ───────────── */
function SyltBeforeAfter({
    eyebrow, title, lead, footnote, pairs,
}: {
    eyebrow: string; title: string; lead: string; footnote: string;
    pairs: Array<{ id: string; title: string; vorher: string; nachher: string }>;
}) {
    return (
        <section
            className="py-24 md:py-32"
            style={{ background: "hsl(var(--hf-surface))" }}
            data-testid="sylt-beforeafter"
        >
            <Container>
                <div className="max-w-3xl mb-14">
                    <div className="hf-eyebrow">{eyebrow}</div>
                    <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                        {title.split("·").map((t, i, arr) => (
                            <span key={i}>
                                {i > 0 && (
                                    <span
                                        className="mx-3"
                                        style={{ color: "hsl(var(--hf-gold-dark))" }}
                                    >
                                        ·
                                    </span>
                                )}
                                {i === arr.length - 1 && arr.length > 1 ? (
                                    <em
                                        className="not-italic"
                                        style={{ color: "hsl(var(--hf-gold-dark))", fontStyle: "italic" }}
                                    >
                                        {t.trim()}
                                    </em>
                                ) : (
                                    t.trim()
                                )}
                            </span>
                        ))}
                    </h2>
                    <p className="mt-6 text-base md:text-lg leading-relaxed text-hf-muted max-w-2xl">
                        {lead}
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {pairs.map((p) => (
                        <div key={p.id} data-testid={`sylt-ba-${p.id}`}>
                            <div
                                className="hf-eyebrow mb-3 inline-flex items-center gap-2"
                                style={{ color: "hsl(var(--hf-gold-dark))" }}
                            >
                                <MoveHorizontal size={12} /> {p.title}
                            </div>
                            <BeforeAfterSlider
                                vorher={p.vorher}
                                nachher={p.nachher}
                                pairTitle={p.title}
                                testId={`sylt-ba-slider-${p.id}`}
                            />
                        </div>
                    ))}
                </div>
                <p
                    className="mt-14 text-xs md:text-sm text-hf-muted italic max-w-3xl"
                    data-testid="sylt-beforeafter-footnote"
                >
                    {footnote}
                </p>
            </Container>
        </section>
    );
}

/**
 * Vorher/Nachher-Slider ohne externe Bibliothek.
 * Range-Input steuert clip-path des Nachher-Layers.
 */
function BeforeAfterSlider({
    vorher, nachher, pairTitle, testId,
}: { vorher: string; nachher: string; pairTitle: string; testId: string }) {
    const [pct, setPct] = useState(50);
    return (
        <div
            className="relative aspect-[3/2] overflow-hidden rounded-sm select-none"
            style={{ background: "hsl(var(--hf-ink))", border: "1px solid hsl(var(--hf-line))" }}
            data-testid={testId}
        >
            {/* Vorher (Basis-Layer) */}
            <img
                src={vorher}
                alt={`${pairTitle} · Vorher`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
            />
            {/* Nachher (per clip-path zugeschnitten) */}
            <img
                src={nachher}
                alt={`${pairTitle} · Nachher`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ clipPath: `inset(0 0 0 ${pct}%)` }}
                draggable={false}
            />
            {/* Trennlinie + Griff */}
            <div
                className="absolute inset-y-0 pointer-events-none"
                style={{
                    left: `${pct}%`,
                    width: 2,
                    background: "hsl(var(--hf-gold))",
                    boxShadow: "0 0 0 1px hsl(var(--hf-ink) / 0.4)",
                }}
            >
                <div
                    className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full flex items-center justify-center"
                    style={{
                        background: "hsl(var(--hf-bg))",
                        border: "2px solid hsl(var(--hf-gold))",
                        boxShadow: "0 6px 18px hsl(var(--hf-ink) / 0.35)",
                    }}
                >
                    <MoveHorizontal size={16} style={{ color: "hsl(var(--hf-gold-dark))" }} />
                </div>
            </div>
            {/* Labels */}
            <span
                className="absolute top-3 left-3 text-[9px] md:text-[10px] tracking-[0.22em] uppercase px-2 py-1 rounded-sm"
                style={{
                    background: "hsl(var(--hf-ink) / 0.78)",
                    color: "hsl(var(--hf-gold-soft))",
                }}
            >
                Vorher
            </span>
            <span
                className="absolute top-3 right-3 text-[9px] md:text-[10px] tracking-[0.22em] uppercase px-2 py-1 rounded-sm"
                style={{
                    background: "hsl(var(--hf-gold))",
                    color: "hsl(var(--hf-ink))",
                }}
            >
                Nachher
            </span>
            {/* Bedienelement (nativer Range-Input, komplett übers Bild, unsichtbar) */}
            <input
                type="range"
                min={0}
                max={100}
                value={pct}
                onChange={(e) => setPct(Number(e.currentTarget.value))}
                aria-label={`${pairTitle} · Vorher/Nachher-Vergleich`}
                className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
                data-testid={`${testId}-input`}
            />
        </div>
    );
}

/* ─────────────  SVG-Sylt-Umriss-Wasserzeichen  ───────────── */
function SyltWatermark() {
    return (
        <svg
            viewBox="0 0 200 400"
            className="absolute right-6 md:right-12 top-24 md:top-32 w-20 md:w-28"
            fill="none"
            stroke="hsl(var(--hf-gold))"
            strokeWidth="1"
            strokeLinejoin="round"
            style={{ opacity: 0.09 }}
            aria-hidden
        >
            <path d="M100,10 C110,20 115,40 108,60 C102,80 105,100 115,120 C125,140 120,165 110,180 C100,195 95,215 100,235 C105,255 105,275 95,290 C85,305 80,320 90,335 C95,345 92,360 85,380 C82,388 88,392 92,390 C95,388 100,392 100,395" />
        </svg>
    );
}

// unused – kept for future ref
void HFLinkButton;
void HFButton;
void SectionHeader;
void BACKEND_URL;

/* ─────────────  INSEL-GALERIE (Phase 2b, jetzt live)  ─────────────
   Rendert `sylt.gallery.tiles[]` via HomeGallery-Komponente (contentKey-Prop).
   Photo360/Video360-Viewer werden pro Kachel-Media-Type automatisch gewählt,
   normale Fotos/Videos landen in der Lightbox. Bei leerem Array →
   Empty-State via HomeGallery. */
function SyltGallery({ eyebrow, headline }: { eyebrow: string; headline: string }) {
    return (
        <section
            className="py-24 md:py-32"
            data-testid="sylt-gallery"
        >
            <Container>
                <div className="max-w-3xl mb-12">
                    <div className="hf-eyebrow">{eyebrow}</div>
                    <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                        {headline}
                    </h2>
                </div>
                <HomeGallery contentKey="sylt.gallery" testIdPrefix="sylt-gallery" />
            </Container>
        </section>
    );
}
